import { useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { CheckCircle2, LifeBuoy, Send } from "lucide-react"
import { useForm } from "react-hook-form"
import { useLocation } from "react-router-dom"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { createSupportRequest } from "@/lib/api"
import { useSession } from "@/lib/session-context"

const supportRequestSchema = z.object({
  category: z.enum(["technical_issue", "account_billing", "product_question", "feedback"]),
  subject: z
    .string()
    .trim()
    .min(5, "Subject must be at least 5 characters.")
    .max(120, "Subject must be at most 120 characters."),
  message: z
    .string()
    .trim()
    .min(20, "Message must be at least 20 characters.")
    .max(5_000, "Message must be at most 5,000 characters."),
})

const defaultValues = {
  category: "technical_issue",
  subject: "",
  message: "",
}

export function SupportDialog({ open, onOpenChange }) {
  const { currentUser, csrfToken } = useSession()
  const location = useLocation()
  const [reference, setReference] = useState(null)
  const form = useForm({
    resolver: zodResolver(supportRequestSchema),
    defaultValues,
  })

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen && form.formState.isSubmitting) return

    onOpenChange(nextOpen)
  }

  const handleSubmit = async (values) => {
    form.clearErrors("root.server")

    try {
      const result = await createSupportRequest(
        {
          ...values,
          sourcePath: `${location.pathname}${location.search}`,
        },
        { csrfToken },
      )
      setReference(result.supportRequest.reference)
    } catch (error) {
      const fieldErrors = error.payload?.fieldErrors ?? {}
      const appliedFieldErrors = Object.entries(fieldErrors).filter(([name]) => name in defaultValues)
      appliedFieldErrors.forEach(([name, messages]) => {
        form.setError(name, { type: "server", message: messages[0] })
      })
      if (appliedFieldErrors.length === 0) {
        form.setError("root.server", { type: "server", message: error.message })
      }
    }
  }

  const isSubmitting = form.formState.isSubmitting

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {reference ? (
          <>
            <DialogHeader className="items-center text-center sm:text-center">
              <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
                <CheckCircle2 className="size-6 text-primary" />
              </div>
              <DialogTitle>Support request received</DialogTitle>
              <DialogDescription>
                Your reference is <span className="font-semibold text-foreground">{reference}</span>.
                Support can reply directly to {currentUser.email}.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="sm:justify-center">
              <Button type="button" onClick={() => handleOpenChange(false)}>Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10">
                  <LifeBuoy className="size-5 text-primary" />
                </div>
                <div>
                  <DialogTitle>Contact Support</DialogTitle>
                  <DialogDescription>Tell us what you need help with.</DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <Form {...form}>
              <form id="support-request-form" onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange} disabled={isSubmitting}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select a category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="technical_issue">Technical issue</SelectItem>
                          <SelectItem value="account_billing">Account / billing</SelectItem>
                          <SelectItem value="product_question">Product question</SelectItem>
                          <SelectItem value="feedback">Feedback</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-2">
                  <Label htmlFor="support-email">Email</Label>
                  <Input id="support-email" value={currentUser.email} type="email" disabled />
                </div>

                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Subject</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Briefly describe the issue"
                          autoComplete="off"
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Message</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="What happened, and what did you expect?"
                          className="min-h-32 resize-y"
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {form.formState.errors.root?.server ? (
                  <p role="alert" className="text-sm text-destructive">
                    {form.formState.errors.root.server.message}
                  </p>
                ) : null}
              </form>
            </Form>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" form="support-request-form" disabled={isSubmitting}>
                <Send className="size-4" />
                {isSubmitting ? "Sending..." : "Send message"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
